import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Avatar, Button, Divider, Drawer, Dropdown, Empty, Input, List, Spin, Typography, message } from 'antd';
import { CheckOutlined, DownOutlined, GlobalOutlined, LogoutOutlined, SearchOutlined, UserOutlined } from '@ant-design/icons';
import { useAuth } from '../context/AuthContext';
import api from '../api/axios';
import '../styles/Navigation.css';

const { Text } = Typography;

function Navigation() {
  const location = useLocation();
  const navigate = useNavigate();
  const enterpriseId = location.pathname.match(/^\/enterprise\/([^/]+)/)?.[1];
  const { logout, user } = useAuth();
  const [custs, setCusts] = useState([]);
  const [loadingCusts, setLoadingCusts] = useState(true);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [search, setSearch] = useState('');

  useEffect(() => {
    let isMounted = true;
    api.get('/customer/GetMyCusts')
      .then(({ data }) => {
        if (!isMounted) return;
        const customers = Array.isArray(data?.custs) ? data.custs : [];
        setCusts(customers);
        const savedId = localStorage.getItem('selected_cust_uuid');
        const requestedCustomer = customers.find((customer) => customer.uuid === enterpriseId);
        const selectedCustomer = requestedCustomer
          || customers.find((customer) => customer.uuid === savedId)
          || customers[0];
        if (selectedCustomer) {
          localStorage.setItem('selected_cust_uuid', selectedCustomer.uuid);
          if (!requestedCustomer) navigate(`/enterprise/${selectedCustomer.uuid}`, { replace: true });
        }
      })
      .catch(() => {
        if (isMounted) message.error('無法載入公司清單，請稍後再試');
      })
      .finally(() => {
        if (isMounted) setLoadingCusts(false);
      });
    return () => { isMounted = false; };
  }, [enterpriseId, navigate]);

  const selectedCust = custs.find((customer) => customer.uuid === enterpriseId);
  const filteredCusts = useMemo(() => {
    const keyword = search.trim().toLocaleLowerCase();
    if (!keyword) return custs;
    return custs.filter((customer) =>
      customer.display_name?.toLocaleLowerCase().includes(keyword)
      || customer.uuid.toLocaleLowerCase().includes(keyword));
  }, [custs, search]);

  const navItems = [
    { suffix: '', legacyPath: '/dashboard', label: '儀表板' },
    { suffix: '/clients', legacyPath: '/dashboard', label: '客戶' },
    { suffix: '/settings', legacyPath: '/settings', label: '設定' },
  ];
  const userName = user?.name || user?.username || user?.email || user?.sub || '使用者';
  const userEmail = user?.email || user?.unique_name || user?.sub || '';
  const showSeparateEmail = userEmail && userEmail.toLocaleLowerCase() !== userName.toLocaleLowerCase();
  const initials = userName.split(/\s+/).filter(Boolean).slice(0, 2)
    .map((part) => part[0]).join('').toUpperCase() || <UserOutlined />;

  const handleSelectCust = (customer) => {
    localStorage.setItem('selected_cust_uuid', customer.uuid);
    setDrawerOpen(false);
    setSearch('');
    navigate(`/enterprise/${customer.uuid}`);
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const accountMenu = {
    items: [
      {
        key: 'account',
        disabled: true,
        label: (
          <div className="account-summary">
            <Avatar size={56}>{initials}</Avatar>
            <div><Text strong>{userName}</Text>{showSeparateEmail && <Text type="secondary">{userEmail}</Text>}</div>
          </div>
        ),
      },
      { type: 'divider' },
      {
        key: 'profile',
        icon: <UserOutlined />,
        label: '個人資料',
        onClick: () => navigate(enterpriseId ? `/enterprise/${enterpriseId}/profile` : '/profile'),
      },
      { key: 'logout', icon: <LogoutOutlined />, label: '登出', onClick: handleLogout },
    ],
  };

  return (
    <>
      <nav className="navbar">
        <div className="navbar-container">
          <div className="navbar-brand"><h1>企業管理系統</h1></div>
          <ul className="navbar-menu">
            {navItems.map((item) => {
              const path = enterpriseId ? `/enterprise/${enterpriseId}${item.suffix}` : item.legacyPath;
              return (
                <li key={item.label}>
                  <button type="button" className={`navbar-link ${location.pathname === path || (item.suffix && location.pathname.startsWith(`${path}/`)) ? 'active' : ''}`} onClick={() => navigate(path)}>
                    {item.label}
                  </button>
                </li>
              );
            })}
          </ul>
          <div className="navbar-right">
            <Button type="text" className="environment-trigger" icon={<GlobalOutlined />} loading={loadingCusts} onClick={() => setDrawerOpen(true)}>
              <span className="environment-trigger-text"><small>公司</small><strong>{selectedCust?.display_name || '選擇公司'}</strong></span>
              <DownOutlined className="environment-chevron" />
            </Button>
            <Dropdown menu={accountMenu} trigger={['click']} placement="bottomRight">
              <Button type="text" className="account-trigger" aria-label="開啟帳號選單"><Avatar>{initials}</Avatar></Button>
            </Dropdown>
          </div>
        </div>
      </nav>

      <Drawer title="選擇公司" placement="right" width={408} open={drawerOpen} onClose={() => setDrawerOpen(false)} className="environment-drawer">
        <Text type="secondary">選擇要管理及查看資料的公司。</Text>
        <Input allowClear prefix={<SearchOutlined />} placeholder="搜尋公司" value={search} onChange={(event) => setSearch(event.target.value)} className="environment-search" />
        <Divider orientation="left">我的公司（{filteredCusts.length}）</Divider>
        {loadingCusts ? <div className="environment-loading"><Spin /></div> : filteredCusts.length ? (
          <List dataSource={filteredCusts} renderItem={(customer) => (
            <List.Item className={`environment-item ${customer.uuid === enterpriseId ? 'selected' : ''}`} onClick={() => handleSelectCust(customer)}>
              <List.Item.Meta avatar={<Avatar shape="square">{customer.display_name?.[0] || '企'}</Avatar>} title={customer.display_name || '未命名公司'} description={customer.uuid} />
              {customer.uuid === enterpriseId && <CheckOutlined className="environment-check" />}
            </List.Item>
          )} />
        ) : <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="找不到公司" />}
      </Drawer>
    </>
  );
}

export default Navigation;
